import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Badge,
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Select from "react-select";

import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import { useAuth } from "../../../hooks/auth";
import { aprovarTabelaFrete } from "../../../services/tabelasEnviadasAdminApi";
import TabelaFreteModal from "./TabelaFreteModal";
import ReajusteModal from "./ReajusteModal";
import AnexosTabela from "./AnexosTabela";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import MetricCardGroup from "../../../components/MetricCardGroup";
import MetricCard from "../../../components/MetricCard";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SectionCard, { fieldCardStyle } from "../../../components/SectionCard";
import TablePagination from "../../../components/TablePagination";
import { AUTOPILOT_COLORS, autopilotSecondaryButtonStyle } from "../../../components/autopilot/styles";
import { reactSelectStyles, reactSelectPortalProps } from "../../../utils/reactSelectStyles";
import { buildRecalculoPresetFromTable, buildRecalculoPreviewRequestFromTable } from "./recalculoImpacto";
import { formatDateLabel, parseDateOnly } from "./dateUtils";

const STATUS_OPTIONS = [
  { value: "1", label: "Ativas" },
  { value: "0", label: "Inativas" },
  { value: "", label: "Todas" },
];

const VIGENCIA_OPTIONS = [
  { value: false, label: "Todas as vigências" },
  { value: true, label: "Somente válidas hoje" },
];

const DEFAULT_STATUS = "1";

// Estados de lista (carregando / erro / vazio) no padrão das telas de referência.
const stateBoxStyle = {
  textAlign: "center",
  padding: "2.5rem 1rem",
  color: AUTOPILOT_COLORS.muted,
};

const stateTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.98rem",
  fontWeight: 700,
  marginBottom: "0.35rem",
};

const mutedCellStyle = { color: AUTOPILOT_COLORS.muted };

const cellHintStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.78rem",
  lineHeight: 1.35,
};

const filterLabelStyle = { color: AUTOPILOT_COLORS.muted };

const previewGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: "0.75rem",
};

const previewLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "0.25rem",
};

const previewValueStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.92rem",
  lineHeight: 1.45,
};

// Banner de feedback/erro por token: acompanha tema claro/escuro e o accent.
const BANNER_TONES = {
  success: {
    background: AUTOPILOT_COLORS.statusSuccessBg,
    borderColor: AUTOPILOT_COLORS.statusSuccessBorder,
    color: AUTOPILOT_COLORS.statusSuccess,
  },
  danger: {
    background: AUTOPILOT_COLORS.statusDangerBg,
    borderColor: AUTOPILOT_COLORS.statusDangerBorder,
    color: AUTOPILOT_COLORS.statusDanger,
  },
  info: {
    background: "var(--theme-accent-glow)",
    borderColor: "var(--theme-accent-border)",
    color: AUTOPILOT_COLORS.accent,
  },
};

function isDeleteInProgressResponse(error, message) {
  const backendCode = error?.response?.data?.code;
  const status = error?.response?.status;
  const normalizedMessage = String(message || "").toLowerCase();

  return backendCode === "delete_in_progress"
    || (status === 409 && normalizedMessage.includes("em andamento"));
}

function isDeleteTimeoutLikeError(error, message) {
  const normalizedMessage = String(message || "").toLowerCase();
  const errorCode = String(error?.code || "").toLowerCase();

  return normalizedMessage.includes("timeout")
    || normalizedMessage.includes("request failed to complete")
    || normalizedMessage.includes("network error")
    || errorCode === "econnaborted";
}

function toDateOnly(value) {
  return parseDateOnly(value);
}

function isValidToday(start, end) {
  const inicio = toDateOnly(start);
  const hoje = toDateOnly(new Date());
  if (!inicio || !hoje) return false;
  const fim = toDateOnly(end);
  return inicio <= hoje && (!fim || fim >= hoje);
}

function isExpired(end) {
  const fim = toDateOnly(end);
  const hoje = toDateOnly(new Date());
  if (!fim || !hoje) return false;
  return fim < hoje;
}

function isFuture(start) {
  const inicio = toDateOnly(start);
  const hoje = toDateOnly(new Date());
  if (!inicio || !hoje) return false;
  return inicio > hoje;
}

// PO1-2905 (H4): tabela ativa que vence nos próximos 30 dias. A tela já
// avisava depois de vencer ("Fora de vigencia"), quando a transportadora já
// sumiu da cotação; o aviso antecipado é o que dá tempo de renegociar.
const DIAS_AVISO_VENCIMENTO = 30;

function diasAteVencer(end) {
  const fim = toDateOnly(end);
  const hoje = toDateOnly(new Date());
  if (!fim || !hoje) return null;
  return Math.round((fim - hoje) / (24 * 60 * 60 * 1000));
}

function getVigenciaMeta(row) {
  if (isValidToday(row.dataValIni, row.dataValFim)) {
    const dias = diasAteVencer(row.dataValFim);
    if (dias !== null && dias >= 0 && dias <= DIAS_AVISO_VENCIMENTO) {
      return {
        label: dias === 0 ? "Vence hoje" : `Vence em ${dias} dia${dias > 1 ? "s" : ""}`,
        color: "warning",
      };
    }
    return { label: "Valida hoje", color: "success" };
  }

  if (isExpired(row.dataValFim)) {
    return { label: "Fora de vigencia", color: "warning" };
  }

  if (isFuture(row.dataValIni)) {
    return { label: "Futura", color: "info" };
  }

  return { label: "Sem vigencia clara", color: "secondary" };
}

function PreviewField({ label, value }) {
  if (!value || value === "-") return null;

  return (
    <div style={fieldCardStyle}>
      <div style={previewLabelStyle}>{label}</div>
      <div style={previewValueStyle}>{value}</div>
    </div>
  );
}

function Banner({ type = "info", message }) {
  if (!message) return null;

  const tone = BANNER_TONES[type] || BANNER_TONES.info;

  return (
    <div
      style={{
        background: tone.background,
        border: `1px solid ${tone.borderColor}`,
        color: tone.color,
        padding: "0.75rem 1rem",
        borderRadius: "var(--everlog-card-radius-sm)",
        marginTop: "1rem",
        fontSize: "0.88rem",
        lineHeight: 1.45,
      }}
    >
      {message}
    </div>
  );
}

function findOption(options, value) {
  return options.find((option) => option.value === value) || options[0];
}

// PO1-2312: o e-mail "tabela aguardando sua aprovação" aponta para
// /app/cadastros/tabelas-frete?tabela=<id>. A tela abre já no recorte
// "aguardando aprovação" (a tabela nasce inativa) com a tabela em foco.
function tabelaDaUrl(search) {
  const n = Number(new URLSearchParams(search || "").get("tabela"));
  return Number.isInteger(n) && n > 0 ? n : null;
}

export default function TabelasFrete() {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const ehEmpresa = user?.setor === "empresa";
  const tabelaAlvoRef = useRef(tabelaDaUrl(location.search));
  const { empresa, transportadora, hasEmpresaSelection } = useCompanies();
  const empresaId = empresa?.value;
  const transportadoraId = transportadora?.value;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [filtroNome, setFiltroNome] = useState("");
  const [filtroId, setFiltroId] = useState("");
  const [filtroStatus, setFiltroStatus] = useState(tabelaAlvoRef.current ? "" : DEFAULT_STATUS);
  // PO1-2312: tabelas cadastradas pela Everlog a partir de um envio da transportadora,
  // inativas até o embarcador aprovar.
  const [filtroAguardando, setFiltroAguardando] = useState(Boolean(tabelaAlvoRef.current));
  const [aprovando, setAprovando] = useState(false);
  // PO1-3886: o card conta TODAS as tabelas aguardando aprovação do embarcador,
  // fora do recorte da lista (que abre só com ativas) e da paginação.
  const [aguardandoTotal, setAguardandoTotal] = useState(null);
  const [aguardandoVersao, setAguardandoVersao] = useState(0);
  const [filtroTipoServico, setFiltroTipoServico] = useState("");
  const [filtroTipoFrete, setFiltroTipoFrete] = useState("");
  const [filtroFreteUnidade, setFiltroFreteUnidade] = useState("");
  const [filtroModal, setFiltroModal] = useState("");
  const [filtroEstadoOrigem, setFiltroEstadoOrigem] = useState("");
  const [filtroCidadeOrigem, setFiltroCidadeOrigem] = useState("");
  const [filtroValidaHoje, setFiltroValidaHoje] = useState(false);
  const [tiposServicoOptions, setTiposServicoOptions] = useState([{ value: "", label: "Todos" }]);
  const [tiposFreteOptions, setTiposFreteOptions] = useState([{ value: "", label: "Todos" }]);
  const [unidadesOptions, setUnidadesOptions] = useState([{ value: "", label: "Todas" }]);
  const [modaisOptions, setModaisOptions] = useState([{ value: "", label: "Todos" }]);
  const [estadosOptions, setEstadosOptions] = useState([{ value: "", label: "Todas as UFs" }]);
  const [cidadesOptions, setCidadesOptions] = useState([{ value: "", label: "Todas as cidades" }]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [reajusteOpen, setReajusteOpen] = useState(false);
  const [reajusteInitialId, setReajusteInitialId] = useState(null);
  const [erro, setErro] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState({});
  const isDeletingRef = useRef(false);

  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedTableDetail, setSelectedTableDetail] = useState(null);
  // PO1-3032: o calc-frete guarda a tabela em cache por 30 min; edições pelo Hub já
  // invalidam sozinhas, o botão cobre alteração feita direto no banco.
  const [cacheBusy, setCacheBusy] = useState(false);
  const handleLimparCache = async (tabelaId) => {
    if (!tabelaId || cacheBusy) return;
    setCacheBusy(true);
    try {
      const { data } = await api.post(`/frete/tabelas/${tabelaId}/limpar-cache`);
      const removidas = Number(data?.chavesRemovidas || 0);
      setFeedback({
        type: "success",
        message: removidas
          ? `Cache do cálculo limpo: ${removidas} consulta(s) desta tabela descartada(s). O próximo recálculo lê a tabela atual.`
          : "Não havia cache desta tabela no cálculo; o próximo recálculo já lê a tabela atual.",
      });
    } catch (error) {
      setFeedback({
        type: "danger",
        message: error?.response?.data?.error || error?.message || "Não foi possível limpar o cache do cálculo.",
      });
    } finally {
      setCacheBusy(false);
    }
  };
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [impactPreview, setImpactPreview] = useState({ loading: false, total: null, error: "" });
  const [previewVersion, setPreviewVersion] = useState(0);

  const buscar = useCallback(
    async (off = 0, overrides = {}) => {
      if (!empresaId) return;

      setLoading(true);
      setErro(null);

      try {
        const status = "status" in overrides ? overrides.status : filtroStatus;
        const nome = "nome" in overrides ? overrides.nome : filtroNome;
        const id = "id" in overrides ? overrides.id : filtroId;
        const idTipoServico =
          "idTipoServico" in overrides ? overrides.idTipoServico : filtroTipoServico;
        const idTipoFrete =
          "idTipoFrete" in overrides ? overrides.idTipoFrete : filtroTipoFrete;
        const idFreteUnidade =
          "idFreteUnidade" in overrides ? overrides.idFreteUnidade : filtroFreteUnidade;
        const idModal = "idModal" in overrides ? overrides.idModal : filtroModal;
        const idEstadoOrigem =
          "idEstadoOrigem" in overrides ? overrides.idEstadoOrigem : filtroEstadoOrigem;
        const idCidadeOrigem =
          "idCidadeOrigem" in overrides ? overrides.idCidadeOrigem : filtroCidadeOrigem;
        const validaHoje =
          "validaHoje" in overrides ? overrides.validaHoje : filtroValidaHoje;
        const aguardando =
          "aguardandoAprovacao" in overrides ? overrides.aguardandoAprovacao : filtroAguardando;
        const limit = "limit" in overrides ? overrides.limit : pageSize;

        const params = {
          idEmbarcador: empresaId,
          limit,
          offset: off,
        };

        if (transportadoraId) params.idTransportadora = transportadoraId;
        const idInformado = String(id).trim();
        if (idInformado) params.id = idInformado;
        if (nome) params.nome = nome;
        // Busca por ID é exata: acha a tabela mesmo inativa, sem depender do status.
        if (status !== "" && !idInformado) params.status = status;
        if (idTipoServico) params.idTipoServico = idTipoServico;
        if (idTipoFrete) params.idTipoFrete = idTipoFrete;
        if (idFreteUnidade) params.idFreteUnidade = idFreteUnidade;
        if (idModal) params.idModal = idModal;
        if (idEstadoOrigem) params.idEstadoOrigem = idEstadoOrigem;
        if (idCidadeOrigem) params.idCidadeOrigem = idCidadeOrigem;
        if (validaHoje) params.validaHoje = true;
        if (aguardando) params.aguardandoAprovacao = 1;

        const response = await api.get("/frete/tabelas", { params });
        const nextRows = response.data.rows || [];

        setRows(nextRows);
        setTotal(response.data.total || 0);
        setOffset(off);
        setPendingDeleteIds((current) => {
          const currentKeys = Object.keys(current);
          if (currentKeys.length === 0) {
            return current;
          }

          const visibleIds = new Set(nextRows.map((item) => String(item.id)));
          const nextPending = currentKeys.reduce((acc, key) => {
            if (visibleIds.has(key)) {
              acc[key] = true;
            }
            return acc;
          }, {});

          return currentKeys.length === Object.keys(nextPending).length ? current : nextPending;
        });
        setSelectedTableId((current) => {
          const alvo = tabelaAlvoRef.current;
          tabelaAlvoRef.current = null;
          const desejado = alvo && nextRows.some((item) => item.id === alvo) ? alvo : current;
          return nextRows.some((item) => item.id === desejado) ? desejado : nextRows[0]?.id || null;
        });
      } catch (e) {
        console.error("[TabelasFrete] erro:", e);
        setRows([]);
        setTotal(0);
        setSelectedTableId(null);
        setSelectedTableDetail(null);
        setPreviewError("");
        setImpactPreview({ loading: false, total: null, error: "" });
        setErro(e.response?.data?.error || e.message || "Erro desconhecido");
      } finally {
        setLoading(false);
      }
    },
    [
      empresaId,
      filtroNome,
      filtroId,
      filtroStatus,
      filtroTipoServico,
      filtroTipoFrete,
      filtroFreteUnidade,
      filtroModal,
      filtroEstadoOrigem,
      filtroCidadeOrigem,
      filtroValidaHoje,
      filtroAguardando,
      pageSize,
      transportadoraId,
    ]
  );

  // PO1-2312: só o embarcador ativa a tabela vinda da transportadora.
  async function handleAprovar(tabelaId) {
    if (!tabelaId || aprovando) return;
    setAprovando(true);
    try {
      await aprovarTabelaFrete(tabelaId);
      setFeedback({
        type: "success",
        message: "Tabela aprovada e ativada. A transportadora foi avisada por e-mail.",
      });
      buscar(offset);
      setAguardandoVersao((v) => v + 1);
    } catch (error) {
      setFeedback({
        type: "danger",
        message: error?.response?.data?.error || error?.message || "Não foi possível aprovar a tabela.",
      });
    } finally {
      setAprovando(false);
    }
  }

  useEffect(() => {
    buscar(0);
  }, [buscar]);

  useEffect(() => {
    if (!empresaId) {
      setAguardandoTotal(null);
      return undefined;
    }
    let cancelado = false;
    const params = { idEmbarcador: empresaId, aguardandoAprovacao: 1, limit: 1, offset: 0 };
    if (transportadoraId) params.idTransportadora = transportadoraId;
    api
      .get("/frete/tabelas", { params })
      .then((response) => {
        if (!cancelado) setAguardandoTotal(Number(response.data?.total) || 0);
      })
      .catch(() => {
        // Sem a contagem, o card volta a mostrar o que está na página.
        if (!cancelado) setAguardandoTotal(null);
      });
    return () => {
      cancelado = true;
    };
  }, [empresaId, transportadoraId, aguardandoVersao]);

  useEffect(() => {
    if (hasEmpresaSelection) return;

    setRows([]);
    setTotal(0);
    setLoading(false);
    setErro(null);
    setFeedback(null);
    setModalOpen(false);
    setEditId(null);
    setReajusteOpen(false);
    setConfirmDeleteId(null);
    setPendingDeleteIds({});
    setSelectedTableId(null);
    setSelectedTableDetail(null);
    setPreviewLoading(false);
    setPreviewError("");
    setImpactPreview({ loading: false, total: null, error: "" });
  }, [hasEmpresaSelection]);

  useEffect(() => {
    let active = true;

    async function loadFilterOptions() {
      try {
        const [tiposServicoResponse, tiposFreteResponse, unidadesResponse, modaisResponse, estadosResponse] =
          await Promise.all([
            api.get("/frete/tipos-servico"),
            api.get("/frete/tipos-frete"),
            api.get("/frete/unidades"),
            api.get("/frete/modais"),
            api.get("/frete/estados"),
          ]);
        if (!active) return;

        setTiposServicoOptions([
          { value: "", label: "Todos" },
          ...(tiposServicoResponse.data || []).map((item) => ({
            value: String(item.id),
            label: item.nome,
          })),
        ]);
        setTiposFreteOptions([
          { value: "", label: "Todos" },
          ...(tiposFreteResponse.data || []).map((item) => ({
            value: String(item.id),
            label: item.nome,
          })),
        ]);
        setUnidadesOptions([
          { value: "", label: "Todas" },
          ...(unidadesResponse.data || []).map((item) => ({
            value: String(item.id),
            label: item.nome,
          })),
        ]);
        setModaisOptions([
          { value: "", label: "Todos" },
          ...(modaisResponse.data || []).map((item) => ({
            value: String(item.id),
            label: item.nome,
          })),
        ]);
        setEstadosOptions([
          { value: "", label: "Todas as UFs" },
          ...(estadosResponse.data || []).map((item) => ({
            value: String(item.id),
            label: item.uf,
          })),
        ]);
      } catch (e) {
        console.error("[TabelasFrete] erro ao carregar filtros auxiliares:", e);
      }
    }

    loadFilterOptions();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function loadCidades() {
      if (!filtroEstadoOrigem) {
        setCidadesOptions([{ value: "", label: "Todas as cidades" }]);
        setFiltroCidadeOrigem("");
        return;
      }

      try {
        const response = await api.get(`/frete/estados/${filtroEstadoOrigem}/cidades`);
        if (!active) return;

        const nextCidades = [
          { value: "", label: "Todas as cidades" },
          ...(response.data || []).map((item) => ({
            value: String(item.id),
            label: item.nome,
          })),
        ];

        setCidadesOptions(nextCidades);
        setFiltroCidadeOrigem((current) =>
          nextCidades.some((item) => item.value === current) ? current : ""
        );
      } catch (e) {
        if (!active) return;
        console.error("[TabelasFrete] erro ao carregar cidades de origem:", e);
        setCidadesOptions([{ value: "", label: "Todas as cidades" }]);
        setFiltroCidadeOrigem("");
      }
    }

    loadCidades();

    return () => {
      active = false;
    };
  }, [filtroEstadoOrigem]);

  useEffect(() => {
    if (!selectedTableId) {
      setSelectedTableDetail(null);
      setPreviewLoading(false);
      setPreviewError("");
      return;
    }

    let active = true;

    async function loadPreview() {
      try {
        setPreviewLoading(true);
        setPreviewError("");
        const response = await api.get(`/frete/tabelas/${selectedTableId}`);

        if (active) {
          setSelectedTableDetail(response.data || null);
        }
      } catch (e) {
        if (active) {
          console.error("[TabelasFrete] erro ao carregar preview:", e);
          setSelectedTableDetail(null);
          setPreviewError(
            e.response?.data?.error ||
              e.message ||
              "Não foi possível carregar o resumo desta tabela."
          );
        }
      } finally {
        if (active) {
          setPreviewLoading(false);
        }
      }
    }

    loadPreview();

    return () => {
      active = false;
    };
  }, [selectedTableId, previewVersion]);

  useEffect(() => {
    const pendingIds = Object.keys(pendingDeleteIds);
    if (pendingIds.length === 0 || deleting) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      buscar(offset);
    }, 8000);

    return () => clearInterval(intervalId);
  }, [buscar, deleting, offset, pendingDeleteIds]);

  useEffect(() => {
    const request = buildRecalculoPreviewRequestFromTable({
      empresaId,
      table: selectedTableId ? { ...(rows.find((item) => item.id === selectedTableId) || {}), ...selectedTableDetail } : null,
    });

    if (!request || !request.canExecute) {
      setImpactPreview({ loading: false, total: null, error: "" });
      return;
    }

    let active = true;
    setImpactPreview({ loading: true, total: null, error: "" });

    api
      .post(request.previewEndpoint, request.body)
      .then((response) => {
        if (!active) return;
        const total = response?.data?.total;
        const parsed = total == null ? 0 : Number(total);
        setImpactPreview({
          loading: false,
          total: Number.isNaN(parsed) ? 0 : parsed,
          error: "",
        });
      })
      .catch((error) => {
        if (!active) return;
        setImpactPreview({
          loading: false,
          total: null,
          error: error?.response?.data?.error || "Nao foi possivel estimar o impacto inicial.",
        });
      });

    return () => {
      active = false;
    };
  }, [empresaId, rows, selectedTableDetail, selectedTableId]);

  async function handleDelete() {
    if (!confirmDeleteId || isDeletingRef.current) return;

    const deleteId = String(confirmDeleteId);
    isDeletingRef.current = true;
    setDeleting(true);
    setErro(null);
    setPendingDeleteIds((current) => ({ ...current, [deleteId]: true }));

    try {
      const nextOffset = rows.length === 1 && offset > 0 ? Math.max(offset - pageSize, 0) : offset;

      await api.delete(`/frete/tabelas/${confirmDeleteId}`);
      setFeedback({ type: "success", message: "Tabela excluída com sucesso." });
      setConfirmDeleteId(null);
      setPendingDeleteIds((current) => {
        const nextPending = { ...current };
        delete nextPending[deleteId];
        return nextPending;
      });
      await buscar(nextOffset);
      setPreviewVersion((current) => current + 1);
    } catch (e) {
      console.error("[TabelasFrete] erro ao excluir tabela:", e);
      const msg = e.response?.data?.error || e.message || "Erro ao excluir tabela.";
      const deleteInProgress = isDeleteInProgressResponse(e, msg);
      const timeoutLike = isDeleteTimeoutLikeError(e, msg);

      if (deleteInProgress || timeoutLike) {
        setFeedback({
          type: "info",
          message: deleteInProgress
            ? "A exclusão já está em andamento no servidor. Aguarde a atualização da lista."
            : "A exclusão foi iniciada, mas a resposta demorou além do esperado. A lista será atualizada automaticamente.",
        });
        setConfirmDeleteId(null);
      } else if (e.response?.status === 409) {
        setFeedback({ type: "danger", message: msg });
        setConfirmDeleteId(null);
        setPendingDeleteIds((current) => {
          const nextPending = { ...current };
          delete nextPending[deleteId];
          return nextPending;
        });
      } else {
        // Falha de exclusão vai para o banner: `erro` derruba a lista inteira
        // (vira o estado "Não foi possível carregar" do ShellCard).
        setFeedback({ type: "danger", message: msg });
        setPendingDeleteIds((current) => {
          const nextPending = { ...current };
          delete nextPending[deleteId];
          return nextPending;
        });
      }
    } finally {
      isDeletingRef.current = false;
      setDeleting(false);
    }
  }

  function handleEdit(id) {
    setEditId(id);
    setModalOpen(true);
  }

  function handleNovo() {
    setEditId(null);
    setModalOpen(true);
  }

  function handleRecalcularImpactados(table, source = "painel_lateral") {
    const preset = buildRecalculoPresetFromTable(table, source, {
      estimativaImpacto: impactPreview.total != null ? impactPreview.total : null,
    });
    if (!preset) return;
    history.push("/app/flow/operacao/recalcular", { recalculoPreset: preset });
  }

  function handleModalClose(saved) {
    setModalOpen(false);
    const closedId = editId;
    setEditId(null);

    if (saved && hasEmpresaSelection) {
      setFeedback({ type: "success", message: "Tabela salva com sucesso." });
      buscar(offset);
      if (closedId) {
        setSelectedTableId(closedId);
      }
      setPreviewVersion((current) => current + 1);
    }
  }

  function handleReajusteClose(novaId, anexosResumo) {
    setReajusteOpen(false);
    setReajusteInitialId(null);

    if (novaId && hasEmpresaSelection) {
      // PO1-2859: o backend copia os anexos da origem para a tabela nova (best-effort).
      const copiados = Number(anexosResumo && anexosResumo.copiados) || 0;
      const falhas = Number(anexosResumo && anexosResumo.falhas) || 0;
      let message = "Nova tabela reajustada criada com sucesso.";
      if (copiados > 0) message += ` ${copiados} anexo(s) herdado(s) da tabela original.`;
      if (falhas > 0) message += ` Atenção: ${falhas} anexo(s) não puderam ser copiados.`;
      setFeedback({ type: falhas > 0 ? "danger" : "success", message });
      buscar(offset);
      setSelectedTableId(novaId);
      setPreviewVersion((current) => current + 1);
      setEditId(novaId);
      setModalOpen(true);
    }
  }

  function handleLimparFiltros() {
    setFiltroNome("");
    setFiltroId("");
    setFiltroStatus(DEFAULT_STATUS);
    setFiltroTipoServico("");
    setFiltroTipoFrete("");
    setFiltroFreteUnidade("");
    setFiltroModal("");
    setFiltroEstadoOrigem("");
    setFiltroCidadeOrigem("");
    setFiltroValidaHoje(false);
    setCidadesOptions([{ value: "", label: "Todas as cidades" }]);
    buscar(0, {
      nome: "",
      id: "",
      status: DEFAULT_STATUS,
      idTipoServico: "",
      idTipoFrete: "",
      idFreteUnidade: "",
      idModal: "",
      idEstadoOrigem: "",
      idCidadeOrigem: "",
      validaHoje: false,
    });
  }

  const currentPage = Math.floor(offset / pageSize) + 1;

  function handlePageChange(page) {
    buscar((page - 1) * pageSize);
  }

  function handlePageSizeChange(size) {
    // `buscar` depende de pageSize, então o efeito de carga refaz a consulta
    // sozinho (na primeira página) — chamar aqui duplicaria a requisição.
    setPageSize(size);
  }

  const selectedRow = useMemo(
    () => rows.find((item) => item.id === selectedTableId) || null,
    [rows, selectedTableId]
  );
  const selectedRowVigencia = useMemo(
    () => (selectedRow ? getVigenciaMeta(selectedRow) : null),
    [selectedRow]
  );

  const visibleStats = useMemo(() => {
    const ativas = rows.filter((item) => item.status).length;
    const inativas = rows.length - ativas;
    const validasHoje = rows.filter((item) => isValidToday(item.dataValIni, item.dataValFim)).length;
    const aguardando = rows.filter((item) => item.aguardandoAprovacao).length;

    return { ativas, inativas, validasHoje, aguardando };
  }, [rows]);

  // Contagem exibida no badge do FilterBand: tudo que desvia do recorte padrão
  // (ativas, sem recorte auxiliar). A busca por nome fica na faixa, não conta aqui.
  const activeFiltersCount = useMemo(
    () =>
      [
        Boolean(String(filtroId).trim()),
        filtroStatus !== DEFAULT_STATUS,
        Boolean(filtroTipoServico),
        Boolean(filtroTipoFrete),
        Boolean(filtroFreteUnidade),
        Boolean(filtroModal),
        Boolean(filtroEstadoOrigem),
        Boolean(filtroCidadeOrigem),
        filtroValidaHoje,
        filtroAguardando,
      ].filter(Boolean).length,
    [
      filtroId,
      filtroStatus,
      filtroTipoServico,
      filtroTipoFrete,
      filtroFreteUnidade,
      filtroModal,
      filtroEstadoOrigem,
      filtroCidadeOrigem,
      filtroValidaHoje,
      filtroAguardando,
    ]
  );

  if (!hasEmpresaSelection) {
    return (
      <div className="content tabelas-frete-page">
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para gerenciar as tabelas de frete." />
      </div>
    );
  }

  const renderListState = () => {
    if (loading) {
      return (
        <div style={stateBoxStyle}>
          <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent, marginRight: "0.5rem" }} />
          Carregando tabelas…
        </div>
      );
    }
    if (erro) {
      return (
        <div style={stateBoxStyle}>
          <div style={stateTitleStyle}>Não foi possível carregar</div>
          <p style={{ marginBottom: "0.75rem" }}>{erro}</p>
          <Button size="sm" color="info" outline onClick={() => buscar(offset)}>
            Tentar novamente
          </Button>
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div style={stateBoxStyle}>
          <div style={stateTitleStyle}>Nenhuma tabela encontrada</div>
          <p style={{ marginBottom: 0 }}>
            {activeFiltersCount > 0 || filtroNome
              ? "Ajuste os filtros ou limpe a busca."
              : "Cadastre a primeira tabela usando o botão acima."}
          </p>
        </div>
      );
    }
    return null;
  };

  const listState = renderListState();

  return (
    <div className="content tabelas-frete-page">
      {modalOpen && <TabelaFreteModal id={editId} onClose={handleModalClose} />}
      {reajusteOpen && <ReajusteModal onClose={handleReajusteClose} initialTabelaId={reajusteInitialId} />}

      <Modal
        isOpen={!!confirmDeleteId}
        toggle={() => {
          if (!deleting) {
            setConfirmDeleteId(null);
          }
        }}
        fade={false}
        modalClassName="modal-align-top"
      >
        <ModalHeader toggle={() => (deleting ? null : setConfirmDeleteId(null))} tag="h5">
          Confirmar exclusão
        </ModalHeader>
        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          <p style={{ marginBottom: "1.25rem" }}>
            Tem certeza que deseja excluir esta tabela? A exclusão não é permitida se a
            tabela já tiver sido utilizada em cálculos de frete.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button
              color="default"
              outline
              size="sm"
              onClick={() => setConfirmDeleteId(null)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button color="danger" size="sm" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Excluindo..." : "Excluir tabela"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <PageHero
        eyebrow="Governança"
        title="Tabelas de frete"
        description="Localize tabelas vigentes, mantenha custos e taxas da base viva e acompanhe o ciclo de vida operacional da malha tarifária."
        actions={
          <>
            <PageHeroRefreshButton onClick={() => buscar(offset)} loading={loading} />
            <Button
              className="btn-sm"
              style={autopilotSecondaryButtonStyle}
              onClick={() => {
                setReajusteInitialId(null);
                setReajusteOpen(true);
              }}
            >
              <i className="tim-icons icon-refresh-02" /> Reajustar
            </Button>
            <Button color="info" className="btn-sm" onClick={handleNovo}>
              <i className="tim-icons icon-simple-add" /> Nova tabela manual
            </Button>
          </>
        }
      />

      <MetricCardGroup
        eyebrow="Recorte operacional"
        title="Resumo do recorte"
        description="Os cards de status e vigência também funcionam como filtro rápido."
      >
        <MetricCard
          label="Carteira no recorte"
          value={total}
          helper="Resultado total da busca atual"
          iconClassName="tim-icons icon-book-bookmark"
          tone="info"
        />
        <MetricCard
          label="Ativas na página"
          value={visibleStats.ativas}
          helper="Status ativo na página atual"
          iconClassName="tim-icons icon-check-2"
          tone="success"
          active={filtroStatus === "1"}
          onClick={() => {
            const nextStatus = filtroStatus === "1" ? "" : "1";
            setFiltroStatus(nextStatus);
            buscar(0, { status: nextStatus });
          }}
        />
        <MetricCard
          label="Inativas na página"
          value={visibleStats.inativas}
          helper="Status inativo na página atual"
          iconClassName="tim-icons icon-alert-circle-exc"
          tone="warning"
          active={filtroStatus === "0"}
          onClick={() => {
            const nextStatus = filtroStatus === "0" ? "" : "0";
            setFiltroStatus(nextStatus);
            buscar(0, { status: nextStatus });
          }}
        />
        <MetricCard
          label="Válidas hoje"
          value={visibleStats.validasHoje}
          helper="Dentro da vigência na página atual"
          iconClassName="tim-icons icon-calendar-60"
          tone="info"
          active={filtroValidaHoje}
          onClick={() => {
            const nextValue = !filtroValidaHoje;
            setFiltroValidaHoje(nextValue);
            buscar(0, { validaHoje: nextValue });
          }}
        />
        <MetricCard
          label="Aguardando sua aprovação"
          value={aguardandoTotal ?? visibleStats.aguardando}
          helper="Cadastradas pela Everlog a partir do envio da transportadora; inativas até você aprovar"
          iconClassName="tim-icons icon-send"
          tone="warning"
          active={filtroAguardando}
          onClick={() => {
            const nextValue = !filtroAguardando;
            // Essas tabelas estão inativas: o recorte padrão "ativas" as esconderia.
            const nextStatus = nextValue ? "" : DEFAULT_STATUS;
            setFiltroAguardando(nextValue);
            setFiltroStatus(nextStatus);
            buscar(0, { aguardandoAprovacao: nextValue, status: nextStatus });
          }}
        />
      </MetricCardGroup>

      <FilterBand
        mode="server"
        searchLabel="Buscar tabela"
        searchHint="Nome da tabela"
        searchPlaceholder="Buscar por nome..."
        searchValue={filtroNome}
        onSearchChange={setFiltroNome}
        onSearchSubmit={(value) => buscar(0, { nome: value })}
        activeFiltersCount={activeFiltersCount}
        panelTitle="Filtros"
        panelDescription="Combine filtros para encontrar a carteira certa, manter vigências e agir só sobre o que está em foco."
        onApply={() => buscar(0)}
        onClear={handleLimparFiltros}
        busy={loading}
      >
        <Row>
          <Col lg="3" md="6">
            <Label for="tf-status" style={filterLabelStyle}>
              Status
            </Label>
            <Select
              inputId="tf-status"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={STATUS_OPTIONS}
              value={findOption(STATUS_OPTIONS, filtroStatus)}
              onChange={(opt) => setFiltroStatus(opt ? opt.value : "")}
              isSearchable={false}
            />
          </Col>
          <Col lg="3" md="6">
            <Label for="tf-vigencia" style={filterLabelStyle}>
              Vigência
            </Label>
            <Select
              inputId="tf-vigencia"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={VIGENCIA_OPTIONS}
              value={findOption(VIGENCIA_OPTIONS, filtroValidaHoje)}
              onChange={(opt) => setFiltroValidaHoje(Boolean(opt && opt.value))}
              isSearchable={false}
            />
          </Col>
          <Col lg="3" md="6">
            <Label for="tf-servico" style={filterLabelStyle}>
              Frete
            </Label>
            <Select
              inputId="tf-servico"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={tiposServicoOptions}
              value={findOption(tiposServicoOptions, filtroTipoServico)}
              onChange={(opt) => setFiltroTipoServico(opt ? opt.value : "")}
            />
          </Col>
          <Col lg="3" md="6">
            <Label for="tf-tipo-frete" style={filterLabelStyle}>
              Tipo de tabela
            </Label>
            <Select
              inputId="tf-tipo-frete"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={tiposFreteOptions}
              value={findOption(tiposFreteOptions, filtroTipoFrete)}
              onChange={(opt) => setFiltroTipoFrete(opt ? opt.value : "")}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: "0.85rem" }}>
          <Col lg="3" md="6">
            <Label for="tf-unidade" style={filterLabelStyle}>
              Unidade
            </Label>
            <Select
              inputId="tf-unidade"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={unidadesOptions}
              value={findOption(unidadesOptions, filtroFreteUnidade)}
              onChange={(opt) => setFiltroFreteUnidade(opt ? opt.value : "")}
            />
          </Col>
          <Col lg="3" md="6">
            <Label for="tf-modal" style={filterLabelStyle}>
              Modal
            </Label>
            <Select
              inputId="tf-modal"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={modaisOptions}
              value={findOption(modaisOptions, filtroModal)}
              onChange={(opt) => setFiltroModal(opt ? opt.value : "")}
            />
          </Col>
          <Col lg="3" md="6">
            <Label for="tf-uf-origem" style={filterLabelStyle}>
              UF origem
            </Label>
            <Select
              inputId="tf-uf-origem"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={estadosOptions}
              value={findOption(estadosOptions, filtroEstadoOrigem)}
              onChange={(opt) => {
                setFiltroEstadoOrigem(opt ? opt.value : "");
                setFiltroCidadeOrigem("");
              }}
            />
          </Col>
          <Col lg="3" md="6">
            <Label for="tf-cidade-origem" style={filterLabelStyle}>
              Cidade origem
            </Label>
            <Select
              inputId="tf-cidade-origem"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={cidadesOptions}
              value={findOption(cidadesOptions, filtroCidadeOrigem)}
              onChange={(opt) => setFiltroCidadeOrigem(opt ? opt.value : "")}
              isDisabled={!filtroEstadoOrigem}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: "0.85rem" }}>
          <Col lg="3" md="6">
            <Label for="tf-id" style={filterLabelStyle}>
              ID da tabela
            </Label>
            <Input
              id="tf-id"
              type="text"
              inputMode="numeric"
              placeholder="Ex.: 1975"
              value={filtroId}
              onChange={(e) => setFiltroId(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  buscar(0);
                }
              }}
            />
          </Col>
        </Row>
      </FilterBand>

      {feedback ? <Banner type={feedback.type} message={feedback.message} /> : null}

      <Row>
        <Col xl="8" lg="7">
          <ShellCard
            className="tabelas-frete-lista"
            eyebrow="Carteira operacional"
            title="Carteira de tabelas"
            description="Clique em uma linha para ler o resumo ao lado ou abra a edição completa para manter dados, custos e taxas."
            action={
              <span style={mutedCellStyle}>
                {total} tabela{total !== 1 ? "s" : ""}
              </span>
            }
          >
            {listState || (
              <TableSurface>
                <Table size="sm" responsive hover className="tabelas-frete-table hub-thead-accent">
                  <thead className="text-primary">
                    <tr>
                      <th>Tabela</th>
                      <th>Escopo</th>
                      <th>Origem</th>
                      <th>Vigência</th>
                      <th style={{ width: "110px" }}>Status</th>
                      <th className="text-right" style={{ width: "110px" }}>
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isSelected = row.id === selectedTableId;
                      const vigencia = getVigenciaMeta(row);
                      const rowDeletePending = Boolean(pendingDeleteIds[String(row.id)]);

                      return (
                        <tr
                          key={row.id}
                          onClick={() => setSelectedTableId(row.id)}
                          style={{
                            cursor: "pointer",
                            background: isSelected
                              ? "var(--everlog-card-surface-active)"
                              : undefined,
                          }}
                        >
                          <td>
                            <div style={{ fontWeight: 600 }}>{row.nome}</div>
                            <div style={cellHintStyle}>
                              #{row.id} • {row.transportadora || "Transportadora não informada"}
                            </div>
                          </td>
                          <td>
                            <div>{row.freteUnidadeCodigo || row.freteUnidade || "—"}</div>
                            <div style={cellHintStyle}>
                              {[row.modal, row.tipoServico || row.servico, row.tipoFrete]
                                .filter(Boolean)
                                .join(" • ") || "—"}
                            </div>
                          </td>
                          <td>
                            {row.ufOrigem
                              ? `${row.ufOrigem}${row.cidadeOrigem ? ` / ${row.cidadeOrigem}` : ""}`
                              : "—"}
                          </td>
                          <td>
                            <div>
                              {formatDateLabel(row.dataValIni)} até {formatDateLabel(row.dataValFim)}
                            </div>
                            <div style={{ marginTop: "0.35rem" }}>
                              <Badge color={vigencia.color}>{vigencia.label}</Badge>
                            </div>
                          </td>
                          <td>
                            <Badge color={row.status ? "success" : "secondary"}>
                              {row.status ? "Ativo" : "Inativo"}
                            </Badge>
                            {row.aguardandoAprovacao ? (
                              <div style={{ marginTop: "0.35rem" }}>
                                <Badge color="warning">Aguardando aprovação</Badge>
                              </div>
                            ) : null}
                          </td>
                          <td className="text-right" onClick={(event) => event.stopPropagation()}>
                            <Button
                              color="link"
                              size="sm"
                              className="btn-icon"
                              title="Editar"
                              onClick={() => handleEdit(row.id)}
                              disabled={rowDeletePending}
                            >
                              <i className="tim-icons icon-pencil" />
                            </Button>
                            <Button
                              color="link"
                              size="sm"
                              className="btn-icon text-danger"
                              title={rowDeletePending ? "Exclusão em andamento" : "Excluir"}
                              onClick={() => setConfirmDeleteId(row.id)}
                              disabled={rowDeletePending}
                            >
                              <i
                                className={
                                  rowDeletePending
                                    ? "tim-icons icon-refresh-01"
                                    : "tim-icons icon-simple-remove"
                                }
                              />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <TablePagination
                  page={currentPage}
                  pageSize={pageSize}
                  total={total}
                  itemLabel="tabela(s)"
                  disabled={loading}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </TableSurface>
            )}
          </ShellCard>
        </Col>

        <Col xl="4" lg="5">
          <ShellCard
            className="tabelas-frete-resumo"
            eyebrow={selectedRow ? "Tabela em foco" : "Resumo"}
            title="Resumo da tabela"
            description="Leitura rápida de vigência, escopo e regras base antes de abrir a manutenção completa."
          >
            {!selectedRow ? (
              <div style={stateBoxStyle}>
                {loading ? "Carregando…" : "Nenhuma tabela selecionada neste recorte."}
              </div>
            ) : (
              <>
                <SectionCard
                  eyebrow={`Tabela em foco • ID ${selectedRow.id}`}
                  title={selectedRow.nome}
                  style={{ marginBottom: "1rem" }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                    <Badge color={selectedRow.status ? "success" : "secondary"}>
                      {selectedRow.status ? "Ativo" : "Inativo"}
                    </Badge>
                    <Badge color={selectedRowVigencia?.color}>{selectedRowVigencia?.label}</Badge>
                    {selectedRow.aguardandoAprovacao ? <Badge color="warning">Aguardando aprovação</Badge> : null}
                  </div>
                  {selectedRow.aguardandoAprovacao ? (
                    <div style={{ marginTop: "0.75rem", fontSize: "0.84rem", color: AUTOPILOT_COLORS.muted }}>
                      A transportadora enviou esta tabela pelo MeuFrete e a Everlog a cadastrou. Ela só passa a valer na
                      auditoria dos CT-es depois que você aprovar.
                      {ehEmpresa ? (
                        <div style={{ marginTop: "0.5rem" }}>
                          <Button color="success" className="btn-sm" disabled={aprovando} onClick={() => handleAprovar(selectedRow.id)}>
                            {aprovando ? "Aprovando..." : "Aprovar e ativar"}
                          </Button>
                        </div>
                      ) : (
                        <div style={{ marginTop: "0.35rem" }}>Só o embarcador ativa esta tabela.</div>
                      )}
                    </div>
                  ) : null}
                </SectionCard>

                {previewLoading ? (
                  <div style={stateBoxStyle}>
                    <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent, marginRight: "0.5rem" }} />
                    Carregando resumo…
                  </div>
                ) : previewError ? (
                  <Banner type="danger" message={previewError} />
                ) : (
                  <div style={previewGridStyle}>
                    <PreviewField label="Transportadora" value={selectedRow.transportadora} />
                    <PreviewField
                      label="Vigência"
                      value={`${formatDateLabel(selectedRow.dataValIni)} até ${formatDateLabel(selectedRow.dataValFim)}`}
                    />
                    <PreviewField
                      label="Unidade / Modal"
                      value={[selectedRow.freteUnidadeCodigo || selectedRow.freteUnidade, selectedRow.modal]
                        .filter(Boolean)
                        .join(" • ")}
                    />
                    <PreviewField
                      label="Frete"
                      value={[selectedRow.servico, selectedRow.tipoServico].filter(Boolean).join(" • ")}
                    />
                    <PreviewField label="Tipo de tabela" value={selectedRow.tipoFrete} />
                    <PreviewField
                      label="Origem"
                      value={
                        selectedRow.ufOrigem
                          ? `${selectedRow.ufOrigem}${selectedRow.cidadeOrigem ? ` / ${selectedRow.cidadeOrigem}` : ""}`
                          : "-"
                      }
                    />
                    <PreviewField
                      label="Fator cubagem"
                      value={
                        selectedTableDetail?.fatorCubagem != null
                          ? String(selectedTableDetail.fatorCubagem)
                          : null
                      }
                    />
                    <PreviewField
                      label="Incide imposto"
                      value={
                        selectedTableDetail?.incideImposto != null
                          ? selectedTableDetail.incideImposto
                            ? "Sim"
                            : "Não"
                          : null
                      }
                    />
                    <PreviewField
                      label="Frete reverso"
                      value={
                        selectedTableDetail?.freteReverso != null
                          ? selectedTableDetail.freteReverso
                            ? "Sim"
                            : "Não"
                          : null
                      }
                    />
                  </div>
                )}

                {!previewLoading && !previewError ? (
                  <AnexosTabela
                    compact
                    tabelaId={selectedRow.id}
                    anexosIniciais={selectedTableDetail ? selectedTableDetail.anexos : undefined}
                    onChange={(lista, mensagem) => {
                      // O painel desmonta enquanto o detalhe recarrega; o feedback vai no banner da lista.
                      if (mensagem) setFeedback({ type: "success", message: mensagem });
                      setPreviewVersion((current) => current + 1);
                    }}
                  />
                ) : null}

                <SectionCard
                  variant="field"
                  eyebrow="Estimativa inicial"
                  style={{ marginTop: "1rem" }}
                >
                  {impactPreview.loading ? (
                    <div style={mutedCellStyle}>Estimando documentos impactados...</div>
                  ) : impactPreview.error ? (
                    <div style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.84rem", lineHeight: 1.45 }}>
                      {impactPreview.error}
                    </div>
                  ) : (
                    <>
                      <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "1.15rem", fontWeight: 700 }}>
                        {impactPreview.total != null ? impactPreview.total : "—"} documento(s)
                      </div>
                      <div style={{ ...mutedCellStyle, fontSize: "0.82rem", marginTop: "0.35rem" }}>
                        Considera a transportadora da tabela, o período sugerido e todos os status do cálculo.
                      </div>
                    </>
                  )}
                </SectionCard>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "1rem" }}>
                  <Button color="info" className="btn-sm" onClick={() => handleEdit(selectedRow.id)}>
                    Editar tabela
                  </Button>
                  <Button
                    className="btn-sm"
                    style={autopilotSecondaryButtonStyle}
                    onClick={() =>
                      handleRecalcularImpactados({ ...selectedRow, ...selectedTableDetail }, "painel_lateral")
                    }
                  >
                    <i className="tim-icons icon-refresh-01" /> Recalcular impactados
                    {impactPreview.total != null ? ` (${impactPreview.total})` : ""}
                  </Button>
                  <Button
                    className="btn-sm"
                    style={autopilotSecondaryButtonStyle}
                    onClick={() => {
                      setReajusteInitialId(selectedRow?.id || null);
                      setReajusteOpen(true);
                    }}
                  >
                    <i className="tim-icons icon-refresh-02" /> Reajustar
                  </Button>
                  <Button
                    className="btn-sm"
                    style={autopilotSecondaryButtonStyle}
                    disabled={cacheBusy}
                    title="O cálculo guarda esta tabela em cache por 30 min. Use depois de alterar a tabela fora do Hub."
                    onClick={() => handleLimparCache(selectedRow?.id)}
                  >
                    <i className="tim-icons icon-trash-simple" />{" "}
                    {cacheBusy ? "Limpando cache..." : "Limpar cache do cálculo"}
                  </Button>
                </div>
              </>
            )}
          </ShellCard>
        </Col>
      </Row>
    </div>
  );
}
